import { EVENT_TYPE, RESOURCE_TYPE, UPDATE_FREQUENCY } from '/b2b/notifications/constants';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import createSchema, {
    CARD_GROUPS,
    SCHEMA_OPTIONS,
    createHelperText,
    getPreferenceKeyPath,
    notificationDefaults,
} from './NotificationForm.schema';

import PropTypes from 'prop-types';
import StickySaveBar from '/b2b/common/components/StickySaveBar';
import ValidationForm from '/b2b/common/components/ValidationForm';
import castArray from 'lodash/castArray';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import makeStyles from '@mui/styles/makeStyles';
import set from 'lodash/set';
import styles from './NotificationForm.styles';

const useStyles = makeStyles(styles);

export const formatPayload = params => {
    const { preferences, ...notificationSettings } = params || {};

    const prefsSchema = createSchema(
        Object.keys(SCHEMA_OPTIONS).filter(key => key.includes('.preferences.'))
    );
    const preferenceLookup = {};
    for (const [key, scheme] of Object.entries(prefsSchema)) {
        const [resourceType, eventType, preference] = getPreferenceKeyPath(key);
        const value = get(preferences, [resourceType, eventType, preference], undefined);
        if (value !== undefined) {
            // If a depends clause is present, check to see that the depends value validates
            if (scheme.depends) {
                let skip = false;
                for (const [dependsOn, ifIsValue] of Object.entries(scheme.depends)) {
                    const dependsValue = get(preferences, getPreferenceKeyPath(dependsOn), false);
                    if (!castArray(ifIsValue).includes(dependsValue)) {
                        // If the depends value is not valid, skip this preference
                        skip = true;
                    }
                }
                if (skip) {
                    continue;
                }
            }
            set(preferenceLookup, [resourceType, eventType, preference], value);
        }
    }
    const settingsArray = Object.entries(notificationSettings).reduce(
        (accumulator, [resourceType, value]) => {
            Object.entries(value).forEach(([eventType, frequency]) => {
                accumulator.push({
                    eventType,
                    resourceType,
                    frequency,
                    preferences: get(preferenceLookup, [resourceType, eventType], null),
                });
            });
            return accumulator;
        },
        []
    );

    const payload = settingsArray.reduce(
        (allSettings, { eventType, resourceType, frequency, preferences }) => {
            if (
                (frequency !== null && (!frequency || !UPDATE_FREQUENCY[frequency])) ||
                (!EVENT_TYPE[eventType] && !RESOURCE_TYPE[resourceType])
            ) {
                return allSettings;
            }
            if (preferences) {
                allSettings.push({
                    resourceType,
                    frequency,
                    preferences,
                });
            } else {
                allSettings.push({
                    resourceType,
                    frequency,
                });
            }

            return allSettings;
        },
        []
    );

    return payload;
};

const NotificationForm = props => {
    const { loading, onSaved, onSubmit, permissions, values } = props;

    const classes = useStyles(props);

    const schema = useMemo(() => createSchema(permissions), [permissions]);
    const helperText = useMemo(() => createHelperText(permissions), [permissions]);

    // The rendered form is seeded from the defaults overlaid with the last-saved
    // values. Both are flat objects keyed by the schema field names (dotted
    // strings), so a flat map is all we need to compare current-vs-saved.
    const baseline = useMemo(() => ({ ...notificationDefaults, ...values }), [values]);

    // `savedValues` is the last-persisted snapshot the dirty check compares
    // against. It follows the `values` prop (initial load / user-view refresh)
    // and is advanced to the just-saved values after a successful save so the
    // admin view — whose `values` prop does not refresh — also settles clean.
    const [savedValues, setSavedValues] = useState(baseline);
    const [workingValues, setWorkingValues] = useState(baseline);
    const [isValid, setIsValid] = useState(true);
    // True while a save is in flight. Used to suppress the grouped cards'
    // loading overlay during save (the save shows feedback via the bar + toast),
    // while still allowing the skeleton on the initial data load.
    const [isSaving, setIsSaving] = useState(false);
    // Bumping this key remounts ValidationForm, forcing it to re-seed its
    // internal state from the last-saved `values` — this is how Discard reverts
    // every control without a network call.
    const [formKey, setFormKey] = useState(0);

    useEffect(() => {
        setSavedValues(baseline);
        setWorkingValues(baseline);
    }, [baseline]);

    const isDirty = useMemo(
        () => !isEqual(workingValues, savedValues),
        [workingValues, savedValues]
    );

    const submitRef = useRef(null);

    const handleOnSubmit = useCallback(
        formValues => {
            const payload = formatPayload(formValues);
            // The container only resolves this promise on a successful save
            // (both the user and admin save paths reject on failure), so the
            // toast + dirty-clear here are success-only.
            return Promise.resolve(onSubmit(payload)).then(response => {
                setSavedValues(workingValues);
                onSaved && onSaved();
                return response;
            });
        },
        [onSaved, onSubmit, workingValues]
    );

    const handleValidate = useCallback(errors => {
        setIsValid(!errors);
    }, []);

    const handleFieldChange = useCallback((field, value) => {
        setWorkingValues(previous => ({ ...previous, [field]: value }));
    }, []);

    const handleSave = useCallback(() => {
        setIsSaving(true);
        return Promise.resolve(submitRef.current && submitRef.current()).finally(() =>
            setIsSaving(false)
        );
    }, []);

    const handleDiscard = useCallback(() => {
        setWorkingValues(savedValues);
        setFormKey(key => key + 1);
    }, [savedValues]);

    return (
        <div className={classes.root}>
            <ValidationForm
                key={formKey}
                grouped
                groups={CARD_GROUPS}
                className={classes.form}
                submitButton={({ onClick }) => {
                    submitRef.current = onClick;
                    return null;
                }}
                schema={schema}
                onSubmit={handleOnSubmit}
                onFieldChange={handleFieldChange}
                onValidate={handleValidate}
                defaultValues={notificationDefaults}
                values={savedValues}
                helperText={helperText}
                isLoading={loading}
                cardLoading={loading && !isSaving}
            />
            <StickySaveBar
                className={classes.saveBar}
                dataTestId="unsaved-changes-bar"
                open={isDirty}
                canSave={isValid}
                onDiscard={handleDiscard}
                onSave={handleSave}
                title="notifications.unsavedBar.title"
                message="notifications.unsavedBar.body"
                discardLabel="notifications.unsavedBar.discard"
                saveLabel="notifications.unsavedBar.save"
            />
        </div>
    );
};

NotificationForm.propTypes = {
    loading: PropTypes.bool,
    onSaved: PropTypes.func,
    onSubmit: PropTypes.func,
    permissions: PropTypes.array,
    values: PropTypes.object,
};

export default NotificationForm;
